/**
 * @appbuilder/shared — the integration contract.
 *
 * Single source of truth for enums and DTOs shared across the DB schema,
 * the Workers API, and the web dashboard. Mirrors APP_BUILDER_BACKEND_DESIGN.md.
 * If you change an enum here, update the DB enum (packages/db) and any UI that
 * renders it. These literal unions intentionally match the Postgres enum labels.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Enums (match Postgres enum labels exactly)
// ─────────────────────────────────────────────────────────────────────────────

export const SUBSCRIPTION_STATUS = [
  'pending', // Stripe `incomplete` / Play SUBSCRIPTION_STATE_PENDING — NO entitlement, do-not-ack
  'active',
  'grace', // past_due / in grace — entitlement for EXISTING work; net-new build requires `active`
  'on_hold',
  'canceled',
  'expired',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[number];

/** Statuses that grant entitlement to REBUILD an existing app. Net-new requires `active`. */
export const ENTITLED_STATUSES: readonly SubscriptionStatus[] = ['active', 'grace'];

export const BILLING_PROVIDER = ['stripe', 'google_play'] as const;
export type BillingProvider = (typeof BILLING_PROVIDER)[number];

export const BUILD_STATUS = [
  'queued',
  'claimed',
  'building',
  'uploading',
  'succeeded',
  'failed',
  'canceled',
] as const;
export type BuildStatus = (typeof BUILD_STATUS)[number];

/** Non-terminal statuses — occupy the active-dedupe slot. */
export const LIVE_BUILD_STATUSES: readonly BuildStatus[] = [
  'queued',
  'claimed',
  'building',
  'uploading',
];

export const BUILD_NODE_STATUS = ['active', 'draining', 'dead'] as const;
export type BuildNodeStatus = (typeof BUILD_NODE_STATUS)[number];

export const ASSET_TYPE = ['icon', 'splash', 'onboarding', 'media', 'other'] as const;
export type AssetType = (typeof ASSET_TYPE)[number];

export const PAGE_KIND = ['native', 'remote', 'custom_html'] as const;
export type PageKind = (typeof PAGE_KIND)[number];

export const DOMAIN_VERIFY_METHOD = ['dns_txt', 'meta_tag', 'file'] as const;
export type DomainVerifyMethod = (typeof DOMAIN_VERIFY_METHOD)[number];

export const DOMAIN_VERIFY_STATUS = ['pending', 'verified', 'failed', 'expired'] as const;
export type DomainVerifyStatus = (typeof DOMAIN_VERIFY_STATUS)[number];

/** How an app's upload keystore is sourced. NULL on an app = not yet chosen (build is blocked). */
export const SIGNING_MODE = ['auto', 'upload'] as const;
export type SigningMode = (typeof SIGNING_MODE)[number];

/** Which studio an app belongs to: 1 = v1 (frozen v1 app-template), 2 = v2 (app-template-v2).
 *  Fixed at create — a v1 app stays v1 forever. Clients that predate X-Client-Version (1.x,
 *  the web dashboard) only ever see generation-1 apps. */
export const APP_GENERATION = [1, 2] as const;
export type AppGeneration = (typeof APP_GENERATION)[number];

/** Lifecycle of a customer-uploaded keystore (validation happens later, on the build node). */
export const KEYSTORE_UPLOAD_STATUS = ['pending_validation', 'validated', 'rejected'] as const;
export type KeystoreUploadStatus = (typeof KEYSTORE_UPLOAD_STATUS)[number];

/** Account membership role (end users). */
export const MEMBER_ROLE = ['owner', 'member'] as const;
export type MemberRole = (typeof MEMBER_ROLE)[number];

/** Super-admin RBAC role (separate auth boundary). */
export const ADMIN_ROLE = ['superadmin', 'admin', 'viewer'] as const;
export type AdminRole = (typeof ADMIN_ROLE)[number];

// ─────────────────────────────────────────────────────────────────────────────
// AppConfig — the per-app config compiled INTO the AAB at build time (no OTA)
// ─────────────────────────────────────────────────────────────────────────────

export interface AppConfig {
  schemaVersion: number;
  identity: {
    name: string;
    androidPackage: string; // e.g. com.acme.app
    versionName: string; // "1.0.0"
    versionCode: number; // strictly increasing per app
    startUrl?: string; // the primary website the app wraps
  };
  theme: {
    darkMode: 'system' | 'light' | 'dark' | 'userToggle';
    colors: Record<string, string>;
    appIconAssetId?: string;
    splashAssetId?: string;
    splashPreset?: string; // predesigned splash template id
    loadingIndicator?: 'spinner' | 'bar' | 'logo' | 'none';
    /** Design personality set by business templates (system fonts only; textures are procedural). */
    font?: 'sans' | 'serif' | 'condensed' | 'casual' | 'cursive' | 'mono';
    shape?: 'sharp' | 'soft' | 'round';
    texture?: 'none' | 'grain' | 'dots' | 'stripes' | 'waves' | 'grid' | 'blobs' | 'rays';
  };
  /** Global app behaviors (Android shell). */
  behavior?: {
    edgeToEdge?: boolean;
    pullToRefresh?: boolean;
    desktopMode?: boolean; // request desktop site in the WebView
    orientation?: 'auto' | 'portrait' | 'landscape';
    keepAwake?: boolean;
  };
  onboarding?: { enabled?: boolean; slides: OnboardingSlide[] };
  navigation: { model: 'bottomTabs' | 'drawer' | 'topTabs' | 'single'; items: NavItem[] };
  pages: PageConfig[];
  push?: {
    enabled: boolean;
    provider: 'onesignal' | 'fcm';
    onesignalAppId?: string;
    onesignalRestApiKey?: string; // sensitive — stored server-side
    topics?: string[];
    promptOnLaunch?: boolean;
  };
  offline?: { enabled: boolean; fallbackPageId?: string; cacheStrategy?: 'cacheFirst' | 'networkFirst' };
  /** Custom offline / no-connection screen. */
  noInternetScreen?: {
    iconAssetId?: string;
    title?: string;
    description?: string;
    autoRetry?: boolean;
  };
  integrations?: {
    /** Floating support button over every page; position defaults to bottomRight. */
    whatsapp?: { enabled: boolean; phone?: string; message?: string; position?: 'bottomRight' | 'bottomLeft' };
  };
  /**
   * The native storefront behind pages of kind "store" and "cart": a priced catalog the customer
   * adds to a basket, checking out to the owner's WhatsApp. Every business template ships one —
   * a bistro takes orders, a salon takes bookings, a plumber takes enquiries — so `tagline` and
   * `cta` carry that trade's wording while the flow stays identical.
   *
   * NOTE: product photos are referenced by `imageAssetId` and the build worker bundles them into
   * the APK (see collectImageAssetIds in worker/index.mjs). Do not strip unknown keys from config
   * on save — this whole section reaches the device verbatim.
   */
  store?: {
    currency?: string;
    whatsapp?: string;
    tagline?: string;
    cta?: string;
    categories?: { id: string; name?: string; emoji?: string }[];
    products?: {
      id: string;
      categoryId?: string;
      name?: string;
      price?: number;
      imageAssetId?: string;
      description?: string;
    }[];
  };
  permissions?: { key: string; rationale: string; requestLazily?: boolean }[];
  /**
   * Optional side navigation drawer. Independent of `navigation.model` — you can have a
   * drawer alongside bottom tabs. Items reuse the NavItem shape (label → page or external URL).
   */
  drawer?: { enabled?: boolean; items: NavItem[] };
  /**
   * Top app bar / header. Independent toggle. `items` are action links shown in the bar
   * (icon/label → page or external URL); the menu button (☰) opens the drawer when enabled.
   */
  header?: {
    enabled?: boolean;
    /** Custom title; empty/omitted → falls back to the app name. */
    title?: string;
    alignment?: 'left' | 'center';
    showLogo?: boolean;
    logoAssetId?: string;
    showMenuButton?: boolean;
    items: NavItem[];
  };
}

export interface OnboardingSlide {
  id: string;
  imageAssetId?: string;
  title: string;
  body: string;
  cta?: { label: string; action: 'next' | 'finish' };
}

export interface NavItem {
  id: string;
  label: string;
  icon?: string;
  target: { pageId: string } | { url: string };
  openExternally?: boolean;
  presentation?: 'inApp' | 'chromeCustomTab' | 'systemBrowser';
}

export interface PageConfig {
  id: string;
  slug: string;
  title: string;
  kind: PageKind;
  native?: { layout: 'cards' | 'list' | 'sidebar' | 'form'; blocks: unknown[] };
  remote?: { url: string; pullToRefresh?: boolean; injectCss?: string };
  customHtml?: { html: string; css?: string; jsEnabled: boolean };
}

// ─────────────────────────────────────────────────────────────────────────────
// API DTOs
// ─────────────────────────────────────────────────────────────────────────────

export interface ApiError {
  error: { code: string; message: string; details?: unknown };
}

export interface AuthTokens {
  accessToken: string;
  expiresInSec: number;
}

export interface AppSummary {
  id: string;
  name: string;
  androidPackage: string;
  currentVersionCode: number | null;
  domainVerified: boolean;
  /** Chosen keystore source, or null until the customer picks (build blocked while null). */
  signingMode: SigningMode | null;
  generation: AppGeneration;
  /** V2 only: app exceeds the account's current paid/free slot limit. */
  locked?: boolean;
  createdAt: string;
}

/** Signing config for an app — mode + metadata for an uploaded keystore (NEVER secrets). */
export interface SigningStatusDto {
  signingMode: SigningMode | null;
  /** Present only when a keystore has been uploaded (mode 'upload'). Passwords are never returned. */
  upload: {
    status: KeystoreUploadStatus;
    originalFilename: string;
    /** Key alias is not secret; store/key passwords are write-only and never serialized. */
    keyAlias: string;
    uploadedAt: string;
    validationError: string | null;
  } | null;
}

export interface SetSigningModeRequest {
  signingMode: SigningMode;
}

export interface AppVersionDto {
  id: string;
  appId: string;
  versionName: string;
  androidVersionCode: number;
  config: AppConfig;
  createdAt: string;
}

export interface CreateAppRequest {
  name: string;
  androidPackage: string;
  config: AppConfig;
}

/** Issued upload target for direct-to-R2 PUT. */
export interface AssetUploadTicket {
  assetId: string;
  uploadUrl: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresAt: string;
}

export interface BuildRequest {
  appVersionId: string;
  /** Optional client idempotency key; combined server-side with token + appVersionId. */
  idempotencyKey?: string;
  /**
   * Initiating device id — the per-device daily build quota (0009) keys on it. Usually
   * supplied via the `X-Device-Id` header (forwarded from the web-handoff token); this
   * body field is the fallback. Absent → the gate falls back to per-account keying.
   */
  deviceId?: string;
}

export interface BuildDto {
  id: string;
  appId: string;
  appVersionId: string;
  status: BuildStatus;
  queuePosition?: number;
  estimatedWaitSeconds?: number;
  aabDownloadUrl?: string;
  apkDownloadUrl?: string;
  logUrl?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EntitlementDto {
  active: boolean;
  status: SubscriptionStatus;
  provider: BillingProvider | null;
  currentPeriodEnd: string | null;
  /** Whether a brand-new app build is allowed (requires `active`, not just `grace`). */
  canBuildNew: boolean;
  /** "App Builder Pro" (lifetime): removes ads + lifts the per-device daily build quota. */
  pro?: boolean;
}

// ── Admin (read-only v1) ─────────────────────────────────────────────────────

export interface AdminFleetNode {
  id: string;
  hostname: string;
  imageVersion: string;
  status: BuildNodeStatus;
  inFlight: number;
  maxConcurrency: number;
  lastHeartbeatAt: string;
}

export interface AdminQueueStats {
  queued: number;
  building: number;
  oldestQueuedAgeSeconds: number;
  p95WaitSeconds: number;
}

export interface AdminOverview {
  users: { total: number; activeSubscribers: number };
  revenue: { mrrCents: number; byProvider: Record<BillingProvider, number> };
  builds: { last24h: number; successRate: number; avgBuildSeconds: number };
}

// ─────────────────────────────────────────────────────────────────────────────
// HTTP error codes used across boundaries
// ─────────────────────────────────────────────────────────────────────────────

export const ERROR_CODES = {
  UNAUTHENTICATED: 'unauthenticated',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  VALIDATION: 'validation_error',
  NO_ENTITLEMENT: 'no_entitlement', // 402
  PREFLIGHT_BLOCKED: 'preflight_blocked', // 422
  DOMAIN_UNVERIFIED: 'domain_unverified',
  RATE_LIMITED: 'rate_limited', // 429
  BUILD_QUOTA_EXCEEDED: 'build_quota_exceeded', // 429 — daily per-device build limit hit (Pro lifts it)
  CONFLICT: 'conflict',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export * from './invitations.js';
